import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TbBolt } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { isElectron } from "./Globals";
import { ROUTES } from "../lib/appRoutes";
import { apiUrl } from "../lib/apiBase";
import { apiCredits } from "../lib/apiRoutes";
import http from "../lib/httpClient";

import { onAuthStateChanged, User } from "firebase/auth";
import { webAuth, mobileAuth as mobileAuthInstance } from "../lib/firebaseApp";
import { isNativeWebView, getShell } from "../lib/apiKey";

import { BrandMark, Mark } from "../design/primitives";
import {
  GROUP_MATCH,
  LEGAL_NAV,
  NavItem,
  PRIMARY_NAV,
  SECONDARY_NAV,
  visibleItems,
} from "../design/nav";
import { useApplyTheme, useShellBodyClasses, useThemePref } from "../design/theme";

export const LANGUAGES: [string, string][] = [
  ["en", "English"],
  ["ko", "한국어"],
  ["ja", "日本語"],
  ["zh-CN", "简体中文"],
  ["zh-TW", "繁體中文"],
  ["es", "Español"],
  ["fr", "Français"],
  ["de", "Deutsch"],
  ["pt", "Português"],
  ["ru", "Русский"],
  ["ar", "العربية"],
  ["hi", "हिन्दी"],
  ["it", "Italiano"],
  ["nl", "Nederlands"],
  ["pl", "Polski"],
  ["sv", "Svenska"],
  ["tr", "Türkçe"],
  ["vi", "Tiếng Việt"],
  ["th", "ไทย"],
  ["id", "Indonesia"],
];

/** Language picker shared by the menu panel and the footer. */
export function LanguageSelect({ id }: { id: string }) {
  const { i18n } = useTranslation();
  const current = i18n.language?.split("-")[0] || "en";
  const value = LANGUAGES.some(([c]) => c === i18n.language) ? i18n.language : current;
  return (
    <label htmlFor={id} className="inline-flex items-center gap-2 text-sm text-ev-muted">
      <span>Language</span>
      <select
        id={id}
        value={value}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        className="min-h-[36px] border border-ev-muted bg-ev-bg px-2 text-sm text-ev-fg"
      >
        {LANGUAGES.map(([code, name]) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Dark / light switch (segmented, aria-pressed). */
export function ThemeToggle() {
  const [pref, setPref] = useThemePref();
  const labelId = React.useId();
  const opt = (p: "dark" | "light", label: string) => (
    <button
      type="button"
      aria-pressed={pref === p}
      onClick={() => setPref(p)}
      className={`min-h-[36px] px-3 text-sm font-medium ${
        pref === p ? "bg-ev-fg text-ev-bg" : "text-ev-fg hover:bg-ev-surface"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex items-center gap-2 text-sm text-ev-muted">
      <span id={labelId}>Theme</span>
      <div role="group" aria-labelledby={labelId} className="inline-flex border border-ev-fg">
        {opt("dark", "Dark")}
        {opt("light", "Light")}
      </div>
    </div>
  );
}

function ItemLink({ item, onNavigate, className = "" }: { item: NavItem; onNavigate: () => void; className?: string }) {
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onNavigate}>
        {item.label} <span aria-hidden="true">↗</span>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    );
  }
  return (
    <Link to={item.to!} className={className} onClick={onNavigate}>
      {item.label}
    </Link>
  );
}

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const shell = getShell();
  const native = isNativeWebView();

  useApplyTheme(location.pathname);
  useShellBodyClasses();

  // Native shells drive navigation from their own chrome (Atomizer AR's
  // tab bar). Expose a router-aware navigate so a tab tap doesn't reload.
  useEffect(() => {
    (window as any).evNavigate = (path: string) => {
      if (typeof path === "string" && path.startsWith("/")) {
        navigate(path);
        window.scrollTo(0, 0);
      }
    };
    return () => {
      delete (window as any).evNavigate;
    };
  }, [navigate]);

  // Atomizer family shells (iOS/iPad AR, Mac, Vision Pro), EV-Mac and
  // Electronify have native auth + membership, so the web Sign in /
  // Member button is suppressed there to avoid duplicates.
  const nativeShellWithChrome =
    shell === "atomizer-ar" ||
    shell === "atomizer-mac" ||
    shell === "atomizer-vision" ||
    shell === "electronvisualized-mac" ||
    shell === "electronify";

  // Shells with their own native credits chip suppress the web pill.
  // Electronify is intentionally NOT here: its WebView has no native top
  // bar, so the pill is the only place credits show while browsing.
  const nativeShellHidesCreditPill =
    shell === "atomizer-ar" ||
    shell === "atomizer-mac" ||
    shell === "atomizer-vision" ||
    shell === "electronvisualized-mac";

  const [menuOpen, setMenuOpen] = useState(false);
  const [logged, setLogged] = useState(false);
  const [creditsBadge, setCreditsBadge] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(() => {
    return typeof window !== "undefined" && window.localStorage.getItem("devMode") === "true";
  });
  const isDevUrl = typeof window !== "undefined" && window.location.hostname.includes("-dev");
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Expose devMode globally so credits system can check it
  useEffect(() => {
    (window as any).__EV_DEV_MODE = devMode;
    window.localStorage.setItem("devMode", devMode ? "true" : "false");
  }, [devMode]);

  // The badge is a pure render of the server's answer. "∞" only when the
  // server says `unlimited` (an active Einsteinium entitlement it could
  // verify, or a server-linked StoreKit transaction). A failed poll keeps
  // the previous badge rather than falling back to a cached "5/5".
  const fetchCredits = (uid?: string, realm?: "mobile" | "web") => {
    http
      .get(apiUrl(apiCredits(uid, realm)))
      .then((r) => {
        const { remaining, total, unlimited, bonus } = r.data || {};
        if (unlimited === true || total === -1) {
          setCreditsBadge("∞");
        } else if (Number.isFinite(remaining) && Number.isFinite(total)) {
          setCreditsBadge(`${remaining}/${total}${bonus ? ` +${bonus}` : ""}`);
        }
      })
      .catch(() => {});
  };

  // Live credit updates — httpClient dispatches this when any
  // credit-protected endpoint returns its updated count via headers.
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      const { remaining, total, unlimited, bonus } = detail as {
        remaining: number;
        total: number;
        unlimited?: boolean;
        bonus?: number;
      };
      if (unlimited === true || total === -1) {
        setCreditsBadge("∞");
      } else if (Number.isFinite(remaining) && Number.isFinite(total)) {
        setCreditsBadge(`${remaining}/${total}${bonus ? ` +${bonus}` : ""}`);
      }
    };
    window.addEventListener("ev-credits-update", onUpdate);
    return () => window.removeEventListener("ev-credits-update", onUpdate);
  }, []);

  useEffect(() => {
    // Watch both web realm (electronvisualized) and mobile realm
    // (atomizer-ar). Either one signed in flips the header to Member.
    let webUser: User | null = null;
    let mobileUser: User | null = null;

    const resolve = () => {
      // Strict per-realm: web user (if any) wins because Stripe-backed
      // entitlements live in the ElectronVisualized RC project; only fall
      // back to mobile realm when no web sign-in exists.
      if (webUser) {
        setLogged(true);
        fetchCredits(webUser.uid, "web");
      } else if (mobileUser) {
        setLogged(true);
        fetchCredits(mobileUser.uid, "mobile");
      } else {
        setLogged(false);
        setCreditsBadge(null);
        fetchCredits();
      }
    };

    const unsubWeb = onAuthStateChanged(webAuth, (u) => {
      webUser = u;
      resolve();
    });
    const unsubMobile = onAuthStateChanged(mobileAuthInstance, (u) => {
      mobileUser = u;
      resolve();
    });

    // Refresh credits every 30s
    const interval = setInterval(() => {
      const webUid = webAuth.currentUser?.uid;
      const mobileUid = mobileAuthInstance.currentUser?.uid;
      if (webUid) fetchCredits(webUid, "web");
      else if (mobileUid) fetchCredits(mobileUid, "mobile");
      else fetchCredits();
    }, 30000);

    return () => {
      clearInterval(interval);
      unsubWeb();
      unsubMobile();
    };
  }, []);

  // Close the menu on navigation.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  // Menu panel: Escape closes, focus moves in on open and back on close,
  // page behind doesn't scroll.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const first = panelRef.current?.querySelector<HTMLElement>("a, button, select");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const movePage = (page: string) => {
    setMenuOpen(false);
    navigate(page);
  };

  const atomizerShell =
    shell === "atomizer-ar" || shell === "atomizer-mac" || shell === "atomizer-vision";
  const brandName = atomizerShell
    ? "Atomizer AR"
    : shell === "electronify"
    ? "Electronify"
    : "ElectronVisualized";

  const showWebAccount = !isElectron() && !nativeShellWithChrome;
  const showNav = !isElectron();
  const showCredits = !!creditsBadge && !devMode && !isElectron() && !nativeShellHidesCreditPill;
  const unlimited = !!creditsBadge && creditsBadge.includes("∞");
  const secondary = visibleItems(SECONDARY_NAV, shell, native);

  const accountButton = (extra = "") =>
    !logged ? (
      <button type="button" onClick={() => movePage(ROUTES.login)} className={`ev-btn ev-btn-outline ev-btn-sm ${extra}`}>
        Sign in
      </button>
    ) : (
      <button type="button" onClick={() => movePage(ROUTES.membership)} className={`ev-btn ev-btn-outline ev-btn-sm ${extra}`}>
        Member
      </button>
    );

  return (
    <>
      <a href="#main" className="ev-skip">
        Skip to content
      </a>
      <header
        className="ev-site-header sticky top-0 z-40 bg-ev-bg text-ev-fg"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          borderBottom: "var(--ev-rule-w) solid var(--ev-rule)",
        }}
      >
        <div className="flex h-16 items-center gap-4 px-4 lg:gap-8 lg:px-10">
          <Link
            to={ROUTES.home}
            className="flex shrink-0 items-center gap-3"
            aria-label={`${brandName}, home`}
            onClick={closeMenu}
          >
            <BrandMark size={16} app={atomizerShell ? "atomizer" : "ev"} />
            <span className="text-[17px] font-semibold tracking-tight">{brandName}</span>
          </Link>

          {showNav && (
            <nav aria-label="Primary" className="hidden h-full items-stretch gap-1 lg:flex">
              {PRIMARY_NAV.map((g) => {
                const active = GROUP_MATCH[g.id]?.test(location.pathname);
                return (
                  <Link
                    key={g.id}
                    to={g.to}
                    aria-current={active ? "page" : undefined}
                    className="group relative flex items-center gap-2 px-3 text-[15px] font-medium"
                  >
                    <Mark glyph={g.glyph} accent={g.accent} size={9} />
                    <span className={active ? "text-ev-fg" : "text-ev-muted group-hover:text-ev-fg"}>{g.label}</span>
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-3 bottom-0 h-[4px] ${active ? "" : "opacity-0 group-hover:opacity-100"}`}
                      style={{ background: `var(--ev-${g.accent})` }}
                    />
                  </Link>
                );
              })}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {devMode && (
              <span className="ev-chip" style={{ borderColor: "var(--ev-yellow)", color: "var(--ev-yellow)" }}>
                DEV MODE
              </span>
            )}
            {isDevUrl && (
              <button type="button" onClick={() => setDevMode(!devMode)} className="ev-chip">
                {devMode ? "DEV" : "dev"}
              </button>
            )}
            {devMode && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    if ("caches" in window) {
                      const keys = await caches.keys();
                      await Promise.all(keys.map((k) => caches.delete(k)));
                    }
                    for (let i = localStorage.length - 1; i >= 0; i--) {
                      const key = localStorage.key(i);
                      if (key && (key.startsWith("mo_") || key.startsWith("density_"))) {
                        localStorage.removeItem(key);
                      }
                    }
                    alert("Cache cleared! Reload the page.");
                  } catch {
                    alert("Cache clear failed.");
                  }
                }}
                className="ev-chip"
                style={{ borderColor: "var(--ev-red)" }}
              >
                Clear cache
              </button>
            )}

            {/* Inline credits chip: plain web and Electronify (no native
                chip there). Click → /credits. */}
            {showCredits && (
              <button
                type="button"
                onClick={() => movePage(ROUTES.credits)}
                className="ev-chip"
                aria-label={unlimited ? "Unlimited credits. See plans and credits" : `${creditsBadge} credits left. See plans and credits`}
              >
                <TbBolt aria-hidden="true" size={17} strokeWidth={1.9} style={{ color: "var(--ev-yellow)" }} />
                <span>{unlimited ? "∞" : creditsBadge}</span>
              </button>
            )}

            {showNav && !native && (
              <Link
                to={`${ROUTES.pricing}?src=header`}
                className="hidden px-2 text-[15px] font-medium text-ev-muted hover:text-ev-fg xl:inline"
              >
                Pricing
              </Link>
            )}

            {showWebAccount && accountButton("hidden sm:inline-flex")}

            {isElectron() && (
              <button type="button" onClick={() => movePage(ROUTES.download)} className="ev-btn ev-btn-ghost ev-btn-sm">
                iOS app
              </button>
            )}

            {showNav && (
              <button
                ref={menuButtonRef}
                type="button"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-expanded={menuOpen}
                aria-controls="ev-menu"
                onClick={() => setMenuOpen((o) => !o)}
                className="ev-btn ev-btn-sm gap-2 border-ev-fg text-ev-fg"
                style={{ borderColor: "var(--ev-fg)", background: menuOpen ? "var(--ev-fg)" : "transparent", color: menuOpen ? "var(--ev-bg)" : "var(--ev-fg)" }}
              >
                <span aria-hidden="true" className="relative inline-block h-3 w-4">
                  <span className={`absolute left-0 right-0 h-[2px] bg-current transition-transform ${menuOpen ? "top-[5px] rotate-45" : "top-0"}`} />
                  <span className={`absolute left-0 right-0 top-[5px] h-[2px] bg-current ${menuOpen ? "opacity-0" : ""}`} />
                  <span className={`absolute left-0 right-0 h-[2px] bg-current transition-transform ${menuOpen ? "top-[5px] -rotate-45" : "top-[10px]"}`} />
                </span>
                <span>Menu</span>
              </button>
            )}
          </div>
        </div>

        {menuOpen && (
          <div
            id="ev-menu"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="fixed inset-x-0 bottom-0 z-50 overflow-y-auto bg-ev-bg text-ev-fg"
            style={{ top: "calc(64px + env(safe-area-inset-top) + var(--ev-rule-w))" }}
          >
            <div className="mx-auto max-w-ev px-4 pb-16 pt-8 lg:px-10">
              {showWebAccount && <div className="mb-8 sm:hidden">{accountButton("w-full")}</div>}

              <nav aria-label="All sections" className="ev-cells grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                {PRIMARY_NAV.map((g) => (
                  <section key={g.id} className="bg-ev-bg p-5" aria-labelledby={`menu-${g.id}`}>
                    <Link to={g.to} onClick={closeMenu} className="group flex items-center gap-3">
                      <Mark glyph={g.glyph} accent={g.accent} size={16} />
                      <h2 id={`menu-${g.id}`} className="ev-h3 group-hover:underline">
                        {g.label}
                      </h2>
                    </Link>
                    <p className="ev-small mt-2">{g.blurb}</p>
                    <ul className="mt-4 space-y-1">
                      {visibleItems(g.items, shell, native).map((it) => (
                        <li key={it.label}>
                          <ItemLink
                            item={it}
                            onNavigate={closeMenu}
                            className="flex min-h-[40px] items-center text-[15px] text-ev-fg hover:underline hover:underline-offset-4"
                          />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </nav>

              <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_auto]">
                <ul className="flex flex-wrap gap-x-6 gap-y-2" aria-label="More">
                  {secondary.map((it) => (
                    <li key={it.label}>
                      <ItemLink item={it} onNavigate={closeMenu} className="inline-flex min-h-[40px] items-center text-base font-medium hover:underline hover:underline-offset-4" />
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-6">
                  <LanguageSelect id="ev-menu-lang" />
                  <ThemeToggle />
                </div>
              </div>

              <ul className="ev-hairline mt-10 flex flex-wrap gap-x-5 gap-y-1 pt-5" aria-label="Legal">
                {LEGAL_NAV.map((it) => (
                  <li key={it.label}>
                    <ItemLink item={it} onNavigate={closeMenu} className="inline-flex min-h-[36px] items-center text-sm text-ev-muted hover:text-ev-fg" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
